import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {
  ApolloHelperService,
  ApolloMutation,
  ApolloMutationResult,
  ApolloQuery,
  MutationAction,
  QueryResult
} from '../../services/apollo-helper.service';
import {GenericCRUDService, GenericTableService, CRUDTableService} from "../index";


@Injectable({
  providedIn: 'root'
})
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export abstract class GenericCRUDTableService<T> extends GenericTableService<T> implements CRUDTableService {

  //Services DI
  protected readonly apolloHelper: ApolloHelperService = inject(ApolloHelperService);
  private readonly genericCRUDService: GenericCRUDService = inject(GenericCRUDService);


  fetchElementById<T>(options: {
    query: ApolloQuery,
    id: string,
    freezeResult?: boolean,
    isLoading$?: BehaviorSubject<boolean>
    convertTo?: undefined
  }): Observable<QueryResult<T>>
  fetchElementById<T>(options: {
    query: ApolloQuery,
    id: string,
    freezeResult?: boolean,
    isLoading$?: BehaviorSubject<boolean>,
    convertTo?: 'observable'
  }): Observable<QueryResult<T>>
  fetchElementById<T>(options: {
    query: ApolloQuery,
    id: string,
    freezeResult?: boolean,
    isLoading$?: BehaviorSubject<boolean>,
    convertTo?: 'promise'
  }): Promise<QueryResult<T>>
  fetchElementById<T>(
    options: {
      query: ApolloQuery,
      id: string,
      freezeResult?: boolean,
      isLoading$?: BehaviorSubject<boolean>,
      convertTo?: 'observable' | 'promise'
    }
  ) {
    if ('promise' === options.convertTo) {
      return this.genericCRUDService.fetchElementById<T>({...options, convertTo: 'promise'});
    }

    return this.genericCRUDService.fetchElementById<T>({...options, convertTo: 'observable'});
  }

  /**
   * It allows mutation to be carried out in a generic way, since we only have to pass
   * the instance that we want to mutate and the action, with this the mutation
   * is automatically generated for us.
   *
   * @param action
   * @param instance
   * @param data
   * @param isLoading$
   * @param enabledSecurity
   * @param mutationSubQuery
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public mutate({action, instance, data, isLoading$, enabledSecurity, mutationSubQueryFields, processResult}: {
    action: MutationAction,
    instance: any,
    data: any,
    isLoading$?: BehaviorSubject<boolean>,
    enabledSecurity?: boolean,
    mutationSubQueryFields?: string,
    processResult?: boolean
  }): Promise<ApolloMutationResult>

  /**
   * It allows mutations to be carried out in the traditional way in which we pass
   * the mutation that we want to apply
   *
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   */
  public mutate({mutation, data, isLoading$, enabledSecurity, processResult}: {
    mutation: ApolloMutation,
    data: any,
    isLoading$?: BehaviorSubject<boolean>,
    enabledSecurity?: boolean
    useInputKeyVar?: boolean
    processResult?: boolean
  }): Promise<ApolloMutationResult>;

  /**
   * @author Carlos Duardo <carlos.duardo@qualud.com>
   * @param options
   */
  public mutate(options: any): Promise<ApolloMutationResult> {
    return this.genericCRUDService.mutate(options);
  }

}
